import clsx from 'clsx';
import type { Ref } from 'react';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import InnerHTMLDiv from '../../common/inner-html-div.component';
import { detectIfTruncated, isObject } from '../../common-functions';
import TooltipLegacy from '../../tooltip-component/tooltip.component';
import { CELL_PADDING_LEFT_PX } from '../data-grid-functions';

const extractObjectValue = (obj: unknown): string => {
  if (!isObject(obj)) return String(obj);

  if ('text' in obj) return String(obj.text);
  if ('value' in obj) return String(obj.value);
  if ('caption' in obj) return String(obj.caption);
  if ('name' in obj) return String(obj.name);
  return String(obj); // [object Object] fallback
};

const renderDataCellValue = (value: unknown): bigint | number | string | undefined => {
  const type = typeof value;
  if (type === 'bigint' || type === 'number' || type === 'string') {
    return value as bigint | number | string;
  }

  if (typeof value === 'boolean') {
    const newValue = value.toString();
    return newValue.charAt(0).toUpperCase() + newValue.substring(1);
  }

  if (Array.isArray(value)) {
    return value
      .filter((item) => item != null)
      .map((item) => extractObjectValue(item))
      .join(', ');
  }

  if (isObject(value)) {
    return extractObjectValue(value);
  }
};

export interface DataGridDataCellProps {
  value: unknown;
  width?: number;
  allowCellScrollBar?: boolean;
  renderAsHTML?: boolean;
  renderAsInnerHTML?: boolean;
  columnId?: string;
  rowId?: string;
  onMeasured?: (columnId: string, width: number, rowId?: string) => void;
  isSizingActive?: boolean; // parent computes: autoSizeColumns && !columnWidthAdjustedRef && isFirstBatch
}

function DataGridDataCell(
  {
    value,
    width,
    allowCellScrollBar,
    renderAsHTML = false,
    renderAsInnerHTML = false,
    columnId,
    rowId,
    onMeasured,
    isSizingActive,
  }: DataGridDataCellProps,
  forwardRef: Ref<HTMLDivElement>
) {
  const cellRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useImperativeHandle(forwardRef, () => cellRef.current as HTMLDivElement);

  useEffect(() => {
    if (contentRef.current) {
      setIsTruncated(detectIfTruncated(contentRef.current));
    }
  }, [width]);
  const cellValue = renderDataCellValue(value);
  const displayValue = cellValue == null ? '' : String(cellValue);

  // Measure content for auto-sizing after layout (only during first sizing session)
  useLayoutEffect(() => {
    if (!isSizingActive) return;
    if (!contentRef.current || !columnId || !onMeasured) return;

    let contentWidth: number;

    if (renderAsInnerHTML) {
      // Special handling for HTML content - needs innerHTML set and width constraints removed
      if (value) {
        // set innerHTML before measuring to ensure correct width
        const strValue = String(value);
        if (contentRef.current.innerHTML !== strValue) {
          contentRef.current.innerHTML = strValue;
        }
      }
      // Remove width constraints for accurate measurement
      const originalCellWidth = cellRef.current?.style.width;
      const originalContentWidth = contentRef.current.style.width;

      if (cellRef.current) cellRef.current.style.width = 'auto';
      contentRef.current.style.width = 'max-content';

      // Force reflow and measure
      contentRef.current.offsetWidth;
      contentWidth = contentRef.current.scrollWidth + CELL_PADDING_LEFT_PX;

      // Restore original widths
      if (cellRef.current && originalCellWidth !== undefined) cellRef.current.style.width = originalCellWidth;
      if (originalContentWidth !== undefined) contentRef.current.style.width = originalContentWidth;
    } else {
      // Simple measurement for regular text content
      contentWidth = contentRef.current.scrollWidth + CELL_PADDING_LEFT_PX;
    }

    if (contentWidth > 0) {
      onMeasured(columnId, contentWidth, rowId);
    }
  }, [isSizingActive, columnId, onMeasured, rowId, value, renderAsInnerHTML]);

  // NOTE: if modifying the padding 'pl-2', please remember to update all other cells as well as 'CELL_PADDING_LEFT_PX'
  const cellClasses = clsx('pl-2', {
    'max-h-20 overflow-y-auto leading-none custom-scroll scrollbar-colored p-1.5 overflow-x-hidden break-words whitespace-pre-line':
      allowCellScrollBar,
  });

  const contentClasses = clsx({
    truncate: !allowCellScrollBar,
  });

  if (renderAsHTML && React.isValidElement(value)) {
    return (
      <div className={cellClasses} ref={cellRef} style={{ width: width ? `${width}px` : undefined }}>
        <div ref={contentRef} className={contentClasses}>
          {value}
        </div>
      </div>
    );
  } else if (renderAsInnerHTML && value) {
    const strValue = String(value);
    return (
      <TooltipLegacy
        description={strValue}
        tooltipClassName="bg-mono-1"
        disabled={!isTruncated || allowCellScrollBar}
        tooltipZIndexCheck={true}
        renderAsInnerHTML
      >
        <div className={cellClasses} ref={cellRef} style={{ width: width ? `${width}px` : undefined }}>
          <InnerHTMLDiv className={contentClasses} ref={contentRef} html={strValue} />
        </div>
      </TooltipLegacy>
    );
  }

  return (
    <TooltipLegacy
      description={displayValue}
      tooltipClassName="bg-mono-1"
      disabled={!isTruncated || allowCellScrollBar}
      tooltipZIndexCheck={true}
    >
      <div className={cellClasses} ref={cellRef} style={{ width: width ? `${width}px` : undefined }}>
        <div ref={contentRef} className={contentClasses}>
          {displayValue}
        </div>
      </div>
    </TooltipLegacy>
  );
}

export default forwardRef(DataGridDataCell);
